/**
 * 培训信息收集管理页面
 * 实现三层UI架构：输入表单 -> 筛选器 -> 数据表格
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Button,
  Table,
  Space,
  message,
  Modal,
  Tooltip,
  Checkbox,
  Row,
  Col,
  Divider,
  Tag,
  Typography,
  Popconfirm,
  Radio,
  Dropdown
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  ExportOutlined,
  FilterOutlined,
  InfoCircleOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { formatBeijingTime } from '../../utils/timeUtils';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  TrainingInfoCollection,
  CreateTrainingInfoInput,
  TrainingInfoQueryParams,
  TrainingInfoFormData,
  TrainingInfoFilters,
  TrainingMode,
  City,
  TrainingBusiness,
  UserRole
} from '../../types';
import {
  createTrainingInfo,
  getTrainingInfoList,
  updateTrainingInfo,
  deleteTrainingInfo,
  exportTrainingInfo,
  getRegionsList,
  getBusinessTypesList,
  getTrainingInfoAttachments,
  downloadTrainingInfoAttachment,
  uploadTrainingInfoAttachments
} from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import AttachmentUploader from '../../components/AttachmentUploader';

// 附件指示器组件 - 显示附件数量和下载按钮
const AttachmentIndicator: React.FC<{ trainingInfoId: number }> = ({ trainingInfoId }) => {
  const [attachments, setAttachments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const loadAttachments = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getTrainingInfoAttachments(trainingInfoId);
      setAttachments(data || []);
    } catch (error) {
      console.error('加载附件失败:', error);
    } finally {
      setLoading(false);
    }
  }, [trainingInfoId]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  const handleDownload = async (attachmentId: number, originalName: string) => {
    try {
      await downloadTrainingInfoAttachment(attachmentId, originalName);
      message.success('下载成功');
    } catch (error: any) {
      message.error(error.response?.data?.message || '下载失败');
    }
  };

  if (loading) {
    return <span style={{ color: '#8c8c8c' }}>加载中...</span>;
  }

  if (attachments.length === 0) {
    return <span style={{ color: '#8c8c8c' }}>无附件</span>;
  }

  // 创建下拉菜单项
  const menuItems = attachments.map((att) => ({
    key: att.id,
    label: (
      <div
        onClick={() => handleDownload(att.id, att.original_name)}
        style={{ cursor: 'pointer' }}
      >
        <DownloadOutlined style={{ marginRight: 8 }} />
        {att.original_name}
      </div>
    ),
  }));

  return (
    <Dropdown menu={{ items: menuItems }} trigger={['click']}>
      <Button
        type="link"
        size="small"
        icon={<DownloadOutlined />}
      >
        {attachments.length}个
      </Button>
    </Dropdown>
  );
};

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

// 培训模式选项
const TRAINING_MODE_OPTIONS: { label: string; value: TrainingMode }[] = [
  { label: '自主培训', value: '自主培训' },
  { label: '合作培训', value: '合作培训' },
  { label: '平台', value: '平台' }
];

// 培训模式选择组件 - 实现互斥规则
const TrainingModeSelector: React.FC<{
  value?: TrainingMode[];
  onChange?: (value: TrainingMode[]) => void;
}> = ({ value = [], onChange }) => {
  const handleChange = (checkedValue: TrainingMode, checked: boolean) => {
    let newValue = [...value];

    if (checked) {
      // 添加选项
      if (!newValue.includes(checkedValue)) {
        newValue.push(checkedValue);

        // 应用互斥规则：自主培训和合作培训不能同时选择
        if (checkedValue === '自主培训' && newValue.includes('合作培训')) {
          newValue = newValue.filter(mode => mode !== '合作培训');
        } else if (checkedValue === '合作培训' && newValue.includes('自主培训')) {
          newValue = newValue.filter(mode => mode !== '自主培训');
        }
      }
    } else {
      // 移除选项
      newValue = newValue.filter(mode => mode !== checkedValue);
    }

    onChange?.(newValue);
  };

  return (
    <Space direction="horizontal" size="middle" wrap>
      {TRAINING_MODE_OPTIONS.map(option => (
        <Checkbox
          key={option.value}
          checked={value.includes(option.value)}
          onChange={(e) => handleChange(option.value, e.target.checked)}
        >
          {option.label}
        </Checkbox>
      ))}
    </Space>
  );
};

interface TrainingInfoCollectionPageProps {}

const TrainingInfoCollectionPage: React.FC<TrainingInfoCollectionPageProps> = () => {
  const { user, isAdmin } = useAuth();

  // 状态管理
  const [form] = Form.useForm<TrainingInfoFormData>();
  const [filterForm] = Form.useForm<TrainingInfoFilters>();
  const [editForm] = Form.useForm<TrainingInfoFormData>();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dataSource, setDataSource] = useState<TrainingInfoCollection[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });

  // 基础数据
  const [regions, setRegions] = useState<City[]>([]);
  const [businessTypes, setBusinessTypes] = useState<TrainingBusiness[]>([]);

  // 弹窗状态
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<TrainingInfoCollection | null>(null);

  // 筛选条件
  const [filters, setFilters] = useState<TrainingInfoFilters>({});

  // 新增：临时存储待上传的附件
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  // 稳定的文件变化回调
  const handleFilesChange = useCallback((files: File[]) => {
    setPendingFiles(files);
  }, []);

  // 加载基础数据
  const loadBasicData = useCallback(async () => {
    try {
      const [regionsData, businessTypesData] = await Promise.all([
        getRegionsList(),
        getBusinessTypesList()
      ]);
      setRegions(regionsData);
      setBusinessTypes(businessTypesData);
    } catch (error) {
      message.error('加载基础数据失败');
      console.error('Error loading basic data:', error);
    }
  }, []);

  // 加载培训信息列表
  const loadTrainingInfoList = useCallback(async (params?: Partial<TrainingInfoQueryParams>) => {
    setLoading(true);
    try {
      const queryParams: TrainingInfoQueryParams = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters,
        ...params
      };

      const response = await getTrainingInfoList(queryParams);
      setDataSource(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.pagination.total,
        current: response.pagination.page
      }));
    } catch (error) {
      message.error('加载数据失败');
      console.error('Error loading training info list:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, filters]);

  // 组件初始化
  useEffect(() => {
    loadBasicData();
  }, [loadBasicData]);

  useEffect(() => {
    loadTrainingInfoList();
  }, [pagination.current, pagination.pageSize, filters]);

  // 创建培训信息
  const handleCreate = async (values: TrainingInfoFormData) => {
    if (!user) {
      message.error('用户未登录');
      return;
    }

    // 验证必填字段
    if (!values.region_id || !values.business_type_id || !values.course_hours ||
        values.average_daily_salary === undefined || values.current_month_classes === undefined ||
        values.current_month_students === undefined || values.next_month_classes === undefined ||
        values.next_month_students === undefined) {
      message.error('请填写所有必填字段');
      return;
    }

    setSubmitting(true);
    try {
      const createData: CreateTrainingInfoInput = {
        region_id: values.region_id,
        business_type_id: values.business_type_id,
        course_hours: values.course_hours,
        training_modes: values.training_modes || [],
        contact: values.contact || '',
        average_daily_salary: values.average_daily_salary,
        current_month_classes: values.current_month_classes,
        current_month_students: values.current_month_students,
        next_month_classes: values.next_month_classes,
        next_month_students: values.next_month_students,
        support_needed: values.support_needed || '',
        collection_month: typeof values.collection_month === 'string'
          ? values.collection_month
          : (values.collection_month as Dayjs).format('YYYY-MM'),
        // 新增字段
        enrollment_fee: values.enrollment_fee || 0,
        subsidy_amount: values.subsidy_amount || 0,
        textbook_type: values.textbook_type || '正版',
        classroom_source: values.classroom_source || '酒店'
      };

      console.log('🚀 Submitting createData:', createData);
      console.log('🔍 Values from form:', values);
      console.log('📎 Pending files:', pendingFiles);
      const createdRecord = await createTrainingInfo(createData);
      console.log('✅ Created record:', createdRecord);
      
      // 如果有待上传的附件，上传它们
      if (pendingFiles.length > 0 && createdRecord.id) {
        console.log(`📤 Uploading ${pendingFiles.length} files for training info ID: ${createdRecord.id}`);
        message.loading('正在上传附件...', 0);
        try {
          const uploadResult = await uploadTrainingInfoAttachments(createdRecord.id, pendingFiles);
          console.log('✅ Upload result:', uploadResult);
          message.destroy();
          message.success(`培训信息创建成功，已上传 ${pendingFiles.length} 个附件`);
          setPendingFiles([]); // 清空待上传文件列表
        } catch (uploadError: any) {
          console.error('❌ Upload error:', uploadError);
          message.destroy();
          message.warning(`培训信息创建成功，但部分附件上传失败：${uploadError.message}`);
        }
      } else {
        console.log('ℹ️ No pending files to upload');
        message.success('培训信息创建成功');
      }
      
      form.resetFields();

      // 重置分页到第一页并刷新列表
      setPagination(prev => ({ ...prev, current: 1 }));
      await loadTrainingInfoList({ page: 1 });
    } catch (error: any) {
      message.error(error.response?.data?.error || '创建失败');
      console.error('Error creating training info:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // 更新培训信息
  const handleUpdate = async (values: TrainingInfoFormData) => {
    if (!editingRecord) return;

    // 验证必填字段
    if (!values.region_id || !values.business_type_id || !values.course_hours ||
        values.average_daily_salary === undefined || values.current_month_classes === undefined ||
        values.current_month_students === undefined || values.next_month_classes === undefined ||
        values.next_month_students === undefined) {
      message.error('请填写所有必填字段');
      return;
    }

    setSubmitting(true);
    try {
      const updateData: Partial<CreateTrainingInfoInput> = {
        region_id: values.region_id,
        business_type_id: values.business_type_id,
        course_hours: values.course_hours,
        training_modes: values.training_modes || [],
        contact: values.contact || '',
        average_daily_salary: values.average_daily_salary,
        current_month_classes: values.current_month_classes,
        current_month_students: values.current_month_students,
        next_month_classes: values.next_month_classes,
        next_month_students: values.next_month_students,
        support_needed: values.support_needed || '',
        collection_month: typeof values.collection_month === 'string'
          ? values.collection_month
          : (values.collection_month as Dayjs).format('YYYY-MM'),
        // 新增字段
        enrollment_fee: values.enrollment_fee || 0,
        subsidy_amount: values.subsidy_amount || 0,
        textbook_type: values.textbook_type || '正版',
        classroom_source: values.classroom_source || '酒店'
      };

      await updateTrainingInfo(editingRecord.id, updateData);
      message.success('培训信息更新成功');
      setEditModalVisible(false);
      setEditingRecord(null);
      editForm.resetFields();

      // 保持当前页面位置并刷新列表
      await loadTrainingInfoList();
    } catch (error: any) {
      message.error(error.response?.data?.error || '更新失败');
      console.error('Error updating training info:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // 删除培训信息
  const handleDelete = async (id: number) => {
    try {
      await deleteTrainingInfo(id);
      message.success('删除成功');

      // 如果当前页没有数据了，回到上一页
      if (dataSource.length === 1 && pagination.current > 1) {
        setPagination(prev => ({ ...prev, current: prev.current - 1 }));
        await loadTrainingInfoList({ page: pagination.current - 1 });
      } else {
        await loadTrainingInfoList();
      }
    } catch (error: any) {
      if (error.response?.status === 403) {
        message.error('权限不足：只能删除自己的记录');
      } else {
        message.error(error.response?.data?.error || '删除失败');
      }
      console.error('Error deleting training info:', error);
    }
  };

  // 导出数据（仅管理员）
  const handleExport = async () => {
    if (!isAdmin) {
      message.error('权限不足：只有管理员可以导出数据');
      return;
    }

    try {
      message.loading('正在导出数据，请稍候...', 0);

      const exportData = await exportTrainingInfo({
        type: 'all',
        filters: filters
      });

      if (!exportData || exportData.length === 0) {
        message.destroy();
        message.warning('暂无数据可导出');
        return;
      }

      // 创建Excel工作簿
      const workbook = XLSX.utils.book_new();

      // 设置工作簿属性
      workbook.Props = {
        Title: `培训信息收集数据_${formatBeijingTime(new Date(), 'YYYY-MM-DD')}`,
        Subject: "培训信息收集数据导出",
        Author: "培训费用标准化检查与预警平台",
        CreatedDate: new Date()
      };

      // 准备表格数据
      const tableData = [
        [
          '序号',
          '地区',
          '业务类型',
          '课时数量',
          '培训模式',
          '联络人',
          '平均日课酬',
          '本月开班数',
          '本月培训人数',
          '下月开班数',
          '下月计划培训人数',
          '招生费（/人）',
          '补贴金额（/人）',
          '教材使用情况',
          '教室来源',
          '收集月份',
          '所需支持',
          '创建时间'
        ],
        ...exportData.map((item: any, index: number) => [
          index + 1,
          item.region_name || '',
          item.business_type_name || '',
          item.course_hours || 0,
          Array.isArray(item.training_modes) ? item.training_modes.join(', ') : item.training_modes,
          item.contact || '',
          item.average_daily_salary || 0,
          item.current_month_classes || 0,
          item.current_month_students || 0,
          item.next_month_classes || 0,
          item.next_month_students || 0,
          item.enrollment_fee || 0,
          item.subsidy_amount || 0,
          item.textbook_type || '',
          item.classroom_source || '',
          item.collection_month || '',
          item.support_needed || '',
          item.created_at ? formatBeijingTime(new Date(item.created_at)) : ''
        ])
      ];

      // 创建工作表
      const worksheet = XLSX.utils.aoa_to_sheet(tableData);

      // 设置列宽
      worksheet['!cols'] = [
        { width: 8 },   // 序号
        { width: 15 },  // 地区
        { width: 25 },  // 业务类型
        { width: 12 },  // 课时数量
        { width: 20 },  // 培训模式
        { width: 15 },  // 联络人
        { width: 15 },  // 平均日课酬
        { width: 12 },  // 本月开班数
        { width: 15 },  // 本月培训人数
        { width: 12 },  // 下月开班数
        { width: 18 },  // 下月计划培训人数
        { width: 15 },  // 招生费
        { width: 15 },  // 补贴金额
        { width: 15 },  // 教材使用情况
        { width: 15 },  // 教室来源
        { width: 15 },  // 收集月份
        { width: 20 },  // 所需支持
        { width: 20 }   // 创建时间
      ];

      // 添加工作表到工作簿
      XLSX.utils.book_append_sheet(workbook, worksheet, '培训信息数据');

      // 生成文件名
      const fileName = `培训信息收集数据_${formatBeijingTime(new Date(), 'YYYY-MM-DD_HH-mm-ss')}.xlsx`;

      // 导出文件
      XLSX.writeFile(workbook, fileName);

      message.destroy();
      message.success(`成功导出 ${exportData.length} 条数据`);
    } catch (error: any) {
      message.destroy();
      message.error(error.response?.data?.error || '导出失败');
      console.error('Error exporting training info:', error);
    }
  };

  // 编辑记录
  const handleEdit = (record: TrainingInfoCollection) => {
    setEditingRecord(record);
    editForm.setFieldsValue({
      region_id: record.region_id,
      business_type_id: record.business_type_id,
      course_hours: record.course_hours,
      training_modes: record.training_modes,
      contact: record.contact,
      average_daily_salary: record.average_daily_salary,
      current_month_classes: record.current_month_classes,
      current_month_students: record.current_month_students,
      next_month_classes: record.next_month_classes,
      next_month_students: record.next_month_students,
      support_needed: record.support_needed || '',
      collection_month: dayjs(record.collection_month), // 转换为 dayjs 对象
      // 新增字段
      enrollment_fee: record.enrollment_fee,
      subsidy_amount: record.subsidy_amount,
      textbook_type: record.textbook_type,
      classroom_source: record.classroom_source
    });
    setEditModalVisible(true);
  };

  // 应用筛选
  const handleFilter = (values: TrainingInfoFilters) => {
    // 处理 dayjs 对象转换为字符串
    const processedValues = { ...values };
    if (processedValues.month && typeof processedValues.month !== 'string') {
      processedValues.month = processedValues.month.format('YYYY-MM');
    }
    setFilters(processedValues);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 重置筛选
  const handleResetFilter = () => {
    filterForm.resetFields();
    setFilters({});
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 刷新数据
  const handleRefresh = () => {
    loadTrainingInfoList();
  };

  // 表格列定义
  const columns: ColumnsType<TrainingInfoCollection> = [
    {
      title: '收集月份',
      dataIndex: 'collection_month',
      key: 'collection_month',
      width: 120,
      sorter: true,
    },
    {
      title: '地区',
      dataIndex: 'region_name',
      key: 'region_name',
      width: 120,
    },
    {
      title: '业务类型',
      dataIndex: 'business_type_name',
      key: 'business_type_name',
      width: 150,
    },
    {
      title: '课时数量',
      dataIndex: 'course_hours',
      key: 'course_hours',
      width: 100,
      align: 'right',
      render: (value) => `${value}课时`,
    },
    {
      title: '培训模式',
      dataIndex: 'training_modes',
      key: 'training_modes',
      width: 180,
      render: (modes: TrainingMode[]) => (
        <Space wrap>
          {modes.map((mode, index) => (
            <Tag key={index} color="blue">{mode}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '联络人',
      dataIndex: 'contact',
      key: 'contact',
      width: 120,
    },
    {
      title: '平均日课酬',
      dataIndex: 'average_daily_salary',
      key: 'average_daily_salary',
      width: 120,
      align: 'right',
      render: (value) => `¥${value.toFixed(2)}`,
    },
    {
      title: '招生费',
      dataIndex: 'enrollment_fee',
      key: 'enrollment_fee',
      width: 100,
      align: 'right',
      render: (value) => `¥${value.toFixed(2)}`,
    },
    {
      title: '补贴金额',
      dataIndex: 'subsidy_amount',
      key: 'subsidy_amount',
      width: 100,
      align: 'right',
      render: (value) => `¥${value.toFixed(2)}`,
    },
    {
      title: '教材类型',
      dataIndex: 'textbook_type',
      key: 'textbook_type',
      width: 100,
      render: (value) => (
        <Tag color={value === '正版' ? 'green' : 'orange'}>
          {value}
        </Tag>
      ),
    },
    {
      title: '教室来源',
      dataIndex: 'classroom_source',
      key: 'classroom_source',
      width: 120,
      render: (value) => {
        const colorMap = {
          '酒店': 'blue',
          '合作机构教室': 'purple',
          '自有教室': 'cyan'
        };
        return (
          <Tag color={colorMap[value as keyof typeof colorMap]}>
            {value}
          </Tag>
        );
      },
    },
    {
      title: '本月',
      children: [
        {
          title: '开班数',
          dataIndex: 'current_month_classes',
          key: 'current_month_classes',
          width: 80,
          align: 'right',
        },
        {
          title: '培训人数',
          dataIndex: 'current_month_students',
          key: 'current_month_students',
          width: 90,
          align: 'right',
        },
      ],
    },
    {
      title: '下月计划',
      children: [
        {
          title: '开班数',
          dataIndex: 'next_month_classes',
          key: 'next_month_classes',
          width: 80,
          align: 'right',
        },
        {
          title: '培训人数',
          dataIndex: 'next_month_students',
          key: 'next_month_students',
          width: 90,
          align: 'right',
        },
      ],
    },
    {
      title: '所需支持',
      dataIndex: 'support_needed',
      key: 'support_needed',
      width: 150,
      ellipsis: {
        showTitle: false,
      },
      render: (text) => (
        <Tooltip title={text} placement="topLeft">
          {text || '-'}
        </Tooltip>
      ),
    },
    {
      title: '附件',
      key: 'attachments',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <AttachmentIndicator trainingInfoId={record.id} />
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (value) => formatBeijingTime(value),
    },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="编辑">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              disabled={Number(record.user_id) !== Number(user?.id)}
            />
          </Tooltip>
          <Tooltip title="删除">
            <Popconfirm
              title="确定要删除这条记录吗？"
              onConfirm={() => handleDelete(record.id)}
              disabled={Number(record.user_id) !== Number(user?.id)}
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                disabled={Number(record.user_id) !== Number(user?.id)}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* 第一层：输入表单 */}
      <Card title="新增培训信息" style={{ marginBottom: 24 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreate}
          initialValues={{
            training_modes: [],
            collection_month: dayjs(),
            // 新增字段默认值
            enrollment_fee: 0,
            subsidy_amount: 0,
            textbook_type: '正版',
            classroom_source: '酒店'
          }}
        >
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                label="地区"
                name="region_id"
                rules={[{ required: true, message: '请选择地区' }]}
              >
                <Select placeholder="选择地区" showSearch optionFilterProp="children" size="small">
                  {regions.map(region => (
                    <Option key={region.id} value={region.id}>
                      {region.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="业务类型"
                name="business_type_id"
                rules={[{ required: true, message: '请选择业务类型' }]}
              >
                <Select placeholder="选择业务类型" showSearch optionFilterProp="children" size="small">
                  {businessTypes.map(type => (
                    <Option key={type.id} value={type.id}>
                      {type.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="课时数量"
                name="course_hours"
                rules={[
                  { required: true, message: '请输入课时数量' },
                  { type: 'number', min: 1, max: 1000, message: '课时数量必须在1-1000之间' }
                ]}
              >
                <InputNumber placeholder="课时数量" style={{ width: '100%' }} size="small" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="收集月份"
                name="collection_month"
                rules={[{ required: true, message: '请选择收集月份' }]}
              >
                <DatePicker.MonthPicker
                  placeholder="选择收集月份"
                  style={{ width: '100%' }}
                  format="YYYY-MM"
                  size="small"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                label="联络人"
                name="contact"
                rules={[{ required: true, message: '请输入联络人' }]}
              >
                <Input placeholder="联络人" size="small" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="兼职师资平均日课酬"
                name="average_daily_salary"
                rules={[
                  { required: true, message: '请输入平均日课酬' },
                  { type: 'number', min: 0, message: '金额不能为负数' }
                ]}
              >
                <InputNumber
                  placeholder="日课酬"
                  style={{ width: '100%' }}
                  formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value!.replace(/¥\s?|(,*)/g, '') as any}
                  size="small"
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="本月开班数量"
                name="current_month_classes"
                rules={[
                  { required: true, message: '请输入本月开班数量' },
                  { type: 'number', min: 0, message: '数量不能为负数' }
                ]}
              >
                <InputNumber placeholder="开班数量" style={{ width: '100%' }} size="small" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="本月培训人数"
                name="current_month_students"
                rules={[
                  { required: true, message: '请输入本月培训人数' },
                  { type: 'number', min: 0, message: '人数不能为负数' }
                ]}
              >
                <InputNumber placeholder="培训人数" style={{ width: '100%' }} size="small" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                label="下月开班数量"
                name="next_month_classes"
                rules={[
                  { required: true, message: '请输入下月开班数量' },
                  { type: 'number', min: 0, message: '数量不能为负数' }
                ]}
              >
                <InputNumber placeholder="开班数量" style={{ width: '100%' }} size="small" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="下月计划培训人数"
                name="next_month_students"
                rules={[
                  { required: true, message: '请输入下月计划培训人数' },
                  { type: 'number', min: 0, message: '人数不能为负数' }
                ]}
              >
                <InputNumber placeholder="培训人数" style={{ width: '100%' }} size="small" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="招生费（/人）"
                name="enrollment_fee"
                rules={[
                  { type: 'number', min: 0, message: '金额不能为负数' }
                ]}
              >
                <InputNumber
                  placeholder="招生费"
                  style={{ width: '100%' }}
                  formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value!.replace(/¥\s?|(,*)/g, '') as any}
                  size="small"
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="补贴金额（/人）"
                name="subsidy_amount"
                rules={[
                  { type: 'number', min: 0, message: '金额不能为负数' }
                ]}
              >
                <InputNumber
                  placeholder="补贴金额"
                  style={{ width: '100%' }}
                  formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value!.replace(/¥\s?|(,*)/g, '') as any}
                  size="small"
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="教材使用情况"
                name="textbook_type"
                rules={[{ required: true, message: '请选择教材使用情况' }]}
              >
                <Radio.Group size="small">
                  <Radio value="正版">正版</Radio>
                  <Radio value="打印">打印</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="教室来源"
                name="classroom_source"
                rules={[{ required: true, message: '请选择教室来源' }]}
              >
                <Select placeholder="选择教室来源" size="small">
                  <Select.Option value="酒店">酒店</Select.Option>
                  <Select.Option value="合作机构教室">合作机构教室</Select.Option>
                  <Select.Option value="自有教室">自有教室</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                label="培训模式"
                name="training_modes"
                rules={[{ required: true, message: '请至少选择一种培训模式' }]}
              >
                <TrainingModeSelector />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="所需支持"
                name="support_needed"
              >
                <Input placeholder="所需支持（可选）" size="small" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="附件上传"
                tooltip="支持 Excel、Word、PDF、压缩包、图片等格式，单个文件不超过 50MB，最多 20 个附件"
              >
                <AttachmentUploader
                  trainingInfoId={undefined}
                  maxCount={20}
                  onFilesChange={handleFilesChange}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              icon={<PlusOutlined />}
            >
              创建培训信息
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* 第二层：筛选器 */}
      <Card title={<Space><FilterOutlined />筛选条件</Space>} style={{ marginBottom: 24 }}>
        <Form
          form={filterForm}
          layout="inline"
          onFinish={handleFilter}
        >
          <Form.Item name="month" label="收集月份">
            <DatePicker.MonthPicker
              placeholder="选择月份"
              format="YYYY-MM"
              allowClear
            />
          </Form.Item>
          <Form.Item name="region_id" label="地区">
            <Select placeholder="选择地区" style={{ width: 120 }} allowClear>
              {regions.map(region => (
                <Option key={region.id} value={region.id}>
                  {region.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="business_type_id" label="业务类型">
            <Select placeholder="选择业务类型" style={{ width: 140 }} allowClear>
              {businessTypes.map(type => (
                <Option key={type.id} value={type.id}>
                  {type.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="training_mode" label="培训模式">
            <Select placeholder="选择培训模式" style={{ width: 120 }} allowClear>
              <Option value="自主培训">自主培训</Option>
              <Option value="合作培训">合作培训</Option>
              <Option value="平台">平台</Option>
            </Select>
          </Form.Item>
          <Form.Item name="search" label="关键词">
            <Input placeholder="搜索联络人或支持内容" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                搜索
              </Button>
              <Button onClick={handleResetFilter}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 第三层：数据表格 */}
      <Card
        title={<Space><InfoCircleOutlined />培训信息列表</Space>}
        extra={
          <Space>
            {isAdmin && (
              <Button
                type="primary"
                ghost
                icon={<ExportOutlined />}
                onClick={handleExport}
              >
                导出数据
              </Button>
            )}
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
            >
              刷新
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
            onChange: (page, pageSize) => {
              setPagination(prev => ({
                ...prev,
                current: page,
                pageSize: pageSize || prev.pageSize
              }));
            }
          }}
          scroll={{ x: 1800 }}
        />
      </Card>

      {/* 编辑弹窗 */}
      <Modal
        title="编辑培训信息"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingRecord(null);
          editForm.resetFields();
        }}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdate}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="地区"
                name="region_id"
                rules={[{ required: true, message: '请选择地区' }]}
              >
                <Select placeholder="请选择地区" showSearch optionFilterProp="children">
                  {regions.map(region => (
                    <Option key={region.id} value={region.id}>
                      {region.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="业务类型"
                name="business_type_id"
                rules={[{ required: true, message: '请选择业务类型' }]}
              >
                <Select placeholder="请选择业务类型" showSearch optionFilterProp="children">
                  {businessTypes.map(type => (
                    <Option key={type.id} value={type.id}>
                      {type.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="课时数量"
                name="course_hours"
                rules={[
                  { required: true, message: '请输入课时数量' },
                  { type: 'number', min: 1, max: 1000, message: '课时数量必须在1-1000之间' }
                ]}
              >
                <InputNumber placeholder="请输入课时数量" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="收集月份"
                name="collection_month"
                rules={[{ required: true, message: '请选择收集月份' }]}
              >
                <DatePicker.MonthPicker
                  placeholder="请选择收集月份"
                  style={{ width: '100%' }}
                  format="YYYY-MM"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="培训模式"
                name="training_modes"
                rules={[{ required: true, message: '请至少选择一种培训模式' }]}
              >
                <TrainingModeSelector />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="联络人"
                name="contact"
                rules={[{ required: true, message: '请输入联络人' }]}
              >
                <Input placeholder="请输入联络人" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="兼职师资平均日课酬"
                name="average_daily_salary"
                rules={[
                  { required: true, message: '请输入平均日课酬' },
                  { type: 'number', min: 0, message: '金额不能为负数' }
                ]}
              >
                <InputNumber
                  placeholder="请输入平均日课酬"
                  style={{ width: '100%' }}
                  formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value!.replace(/¥\s?|(,*)/g, '') as any}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="本月开班数量"
                name="current_month_classes"
                rules={[
                  { required: true, message: '请输入本月开班数量' },
                  { type: 'number', min: 0, message: '数量不能为负数' }
                ]}
              >
                <InputNumber placeholder="请输入开班数量" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="本月培训人数"
                name="current_month_students"
                rules={[
                  { required: true, message: '请输入本月培训人数' },
                  { type: 'number', min: 0, message: '人数不能为负数' }
                ]}
              >
                <InputNumber placeholder="请输入培训人数" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="下月开班数量"
                name="next_month_classes"
                rules={[
                  { required: true, message: '请输入下月开班数量' },
                  { type: 'number', min: 0, message: '数量不能为负数' }
                ]}
              >
                <InputNumber placeholder="请输入开班数量" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="下月计划培训人数"
                name="next_month_students"
                rules={[
                  { required: true, message: '请输入下月计划培训人数' },
                  { type: 'number', min: 0, message: '人数不能为负数' }
                ]}
              >
                <InputNumber placeholder="请输入培训人数" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="招生费（/人）"
                name="enrollment_fee"
                rules={[
                  { type: 'number', min: 0, message: '金额不能为负数' }
                ]}
              >
                <InputNumber
                  placeholder="请输入招生费"
                  style={{ width: '100%' }}
                  formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value!.replace(/¥\s?|(,*)/g, '') as any}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="补贴金额（/人）"
                name="subsidy_amount"
                rules={[
                  { type: 'number', min: 0, message: '金额不能为负数' }
                ]}
              >
                <InputNumber
                  placeholder="请输入补贴金额"
                  style={{ width: '100%' }}
                  formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value!.replace(/¥\s?|(,*)/g, '') as any}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="教材使用情况"
                name="textbook_type"
                rules={[{ required: true, message: '请选择教材使用情况' }]}
              >
                <Radio.Group>
                  <Radio value="正版">正版</Radio>
                  <Radio value="打印">打印</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="教室来源"
                name="classroom_source"
                rules={[{ required: true, message: '请选择教室来源' }]}
              >
                <Select placeholder="请选择教室来源">
                  <Select.Option value="酒店">酒店</Select.Option>
                  <Select.Option value="合作机构教室">合作机构教室</Select.Option>
                  <Select.Option value="自有教室">自有教室</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="所需支持"
            name="support_needed"
          >
            <Input.TextArea
              placeholder="请输入所需支持内容（可选）"
              rows={3}
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item
            label="培训资料附件"
            tooltip="支持 Excel、Word、PDF、压缩包等格式，单个文件不超过 50MB"
          >
            <AttachmentUploader
              trainingInfoId={editingRecord?.id}
              maxCount={20}
              maxSize={50 * 1024 * 1024}
            />
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button
                onClick={() => {
                  setEditModalVisible(false);
                  setEditingRecord(null);
                  editForm.resetFields();
                }}
              >
                取消
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
              >
                保存
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TrainingInfoCollectionPage;